import { graphql, useStaticQuery } from "gatsby";
import type { RefObject } from "react";
import React from "react";

import type { NavQueryQuery } from "../../graphql-types";

import cx from "classnames";
import ExternalLink from "./ExternalLink";
import InternalLink from "./InternalLink";

const STATIC_QUERY = graphql`
  query NavQuery {
    website {
      navLinks {
        ... on Message {
          path
          title
        }
        ... on Subject {
          path
          title: navigationTitle
        }
        ... on Hyperlink {
          path: url
          title
        }
        ... on SustainabilityLandingPage {
          path
          title
        }
        ... on SignUpFormWidget {
          path
          title
        }
        ... on ContactUs {
          path
          title
        }
        ... on Faq {
          path
          title
        }
        ... on Variant {
          path
          title: htmlTitle
        }
        ... on SustainabilityCategory {
          path
          title
        }
        ... on LandingPage {
          path
          title
        }
      }
    }
  }
`;

type Props = {
  absolutePath: string;
  navToggleRef: RefObject<HTMLInputElement>;
  trackHamburgerClickedOpen: () => void;
};

export default function Nav({
  absolutePath,
  navToggleRef,
  trackHamburgerClickedOpen,
}: Props) {
  // TODO: move this to GQL Fragment instead of Static Query
  const data = useStaticQuery<NavQueryQuery>(STATIC_QUERY);
  const { website } = data;
  if (!website) {
    throw new Error("Could not find website node");
  }

  const { navLinks } = website;

  if (navLinks.length === 0) {
    return null;
  }

  return (
    <>
      <label
        data-testid="hamburger-menu"
        data-cy="hamburger-menu"
        tabIndex={0}
        htmlFor="nav-toggle"
        className="hamburger lg:hidden"
        aria-controls="nav-list"
        aria-label="Navigation"
        onKeyDown={({ keyCode, which }) => {
          // todo: replace with https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key
          if (navToggleRef.current && 13 === (which || keyCode)) {
            navToggleRef.current.checked = !navToggleRef.current.checked;
            if (navToggleRef.current.checked) {
              trackHamburgerClickedOpen();
            }
          }
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="64"
          height="64"
          aria-hidden={true}
        >
          <path
            fill="none"
            d="M16 32h32s11.723-.306 10.75-11c-.25-2.75-1.644-4.971-2.869-7.151C50.728 7.08 42.767 2.569 33.733 2.054 33.159 2.033 32.599 2 32 2 15.432 2 2 15.432 2 32c0 16.566 13.432 30 30 30 16.566 0 30-13.434 30-30C62 15.5 48.5 2 32 2S1.875 15.5 1.875 32"
          />
        </svg>
      </label>
      <nav className="hidden pt-20 lg:flex lg:ml-auto lg:mr-4 lg:pt-0 lg:items-center">
        <ul
          className="lg:flex lg:flex-nowrap"
          id="nav-list"
          data-cy="primary-nav-list"
        >
          {navLinks.map(({ path, __typename, title }) => {
            const LinkComponent =
              __typename === "Hyperlink" ? ExternalLink : InternalLink;
            const active = absolutePath.includes(path);
            return (
              <li
                key={path + title}
                className="flex lg:inline-flex lg:ml-8 first:lg:ml-0 py-2 lg:py-0"
              >
                <LinkComponent
                  href={path}
                  className={cx(
                    "relative text-2xl font-semibold mx-auto lg:text-[.9375rem] text-center border-b border-transparent hover:border-current",
                    {
                      "text-[color:var(--nav-active-color)] hover:text-[color:var(--nav-link-color)] border-current":
                        active,
                    },
                    {
                      "text-[color:var(--nav-link-color)] hover:text-[color:var(--nav-active-color)]":
                        !active,
                    }
                  )}
                >
                  {title}
                </LinkComponent>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
}
